import { useCallback, useMemo, useState } from "react";
import { message } from "antd";

import { AppStrings } from "../../../../../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../../../../../utils/ErrorUtils";
import { useIncrementGamePassUserTask } from "../../../../../../queries/game-pass/useIncrementGamePassUserTask";
import { usePassUserTask } from "../../../../../../queries/game-pass/usePassUserTask";
import { useGetGamePassUserStreakHistory } from "../../../../../../queries/game-pass/useGetGamePassUserStreakHistory";
import MonthlyChallengeCard from "./MonthlyChallengeCard";
import MonthlyTierProgressBar from "./MonthlyTierProgressBar";
import MonthlyHistorySection from "./MonthlyHistorySection";
import EditProgressModal, {
  EDIT_MODE_STEPS,
} from "./EditProgressModal";
import ReadOnlyChallengeCard from "./ReadOnlyChallengeCard";
import LoginStreakSummaryStrip from "./LoginStreakSummaryStrip";
import {
  decorateLoginStreakHistoryRows,
  LoginStreakHistoryLegend,
} from "./loginStreakHistoryCells";
import {
  formatTierRewardLabel,
  getNextRewardSummary,
  getTierProgressSummary,
  hasMonthlyTierClaims,
  isMonthlyRewardClaimed,
} from "../monthlyChallengeHelpers";
import {
  buildLoginStreakSubtitle,
  buildLoginStreakSummary,
  buildLoginStreakHistoryRows,
  extractStreakHistoryDays,
  extractStreakResetHour,
  getLoginResetTimeLabel,
  LOGIN_STREAK_HISTORY_SORT_KEYS,
} from "../loginStreakHistory";
import useRewardCurrencyLabel, {
  useRewardSecondaryCurrencyLabel,
} from "../useRewardCurrencyLabel";

const LiveMonthlyChallengeCard = ({
  challengeId,
  progressEntry,
  task,
  progressTask,
  userId,
  campaignId,
  readOnly = false,
  historyColumns = [],
  historyRows = [],
  historyEmptyText = AppStrings.USER_GAME_PASS_MONTHLY_HISTORY_EMPTY,
  historyLabel,
  historySearchPlaceholder,
  historySearchFilters,
  historyCountedFilterLabel,
  isLoginChallenge = false,
  testId,
}) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [viewNow, setViewNow] = useState(() => Date.now());
  const handleWindowExpired = useCallback(() => {
    setViewNow(Date.now());
  }, []);
  const rewardCurrencyLabel = useRewardCurrencyLabel();
  const rewardSecondaryCurrencyLabel = useRewardSecondaryCurrencyLabel();
  const incrementMutation = useIncrementGamePassUserTask();
  const passMutation = usePassUserTask();
  const taskId = task?.id || task?.task_id || progressTask?.task_id;
  const streakHistoryQuery = useGetGamePassUserStreakHistory(userId, taskId, {
    enabled: Boolean(isLoginChallenge && userId && taskId),
  });
  const streakHistoryPayload = streakHistoryQuery.data;
  const streakDays = useMemo(
    () => extractStreakHistoryDays(streakHistoryPayload),
    [streakHistoryPayload],
  );
  const streakResetHour =
    extractStreakResetHour(streakHistoryPayload) ??
    progressTask?.streak_reset_hour ??
    task?.streak_reset_hour;

  const tiers = useMemo(
    () => task?.tiers || progressTask?.tiers || [],
    [task?.tiers, progressTask?.tiers],
  );
  const liveStreak = Number(progressTask?.streak_count);
  const bestStreak = Number(progressTask?.streak_best);
  const rawCount = Number(progressTask?.count) || 0;
  const count = isLoginChallenge
    ? (Number.isFinite(liveStreak) ? liveStreak : rawCount)
    : rawCount;
  const unlockCount = isLoginChallenge
    ? (Number.isFinite(bestStreak) ? bestStreak : count)
    : count;
  const goal =
    Number(task?.target_value) ||
    Number(progressTask?.target_value) ||
    Number(tiers[tiers.length - 1]?.threshold) ||
    0;

  let currentTier = 0;
  if (progressTask?.manually_passed) {
    currentTier = tiers.length;
  } else {
    for (let index = 0; index < tiers.length; index += 1) {
      const threshold = Number(tiers[index]?.threshold);
      if (!Number.isFinite(threshold) || unlockCount < threshold) {
        break;
      }
      currentTier = index + 1;
    }
  }

  const milestones = useMemo(
    () => {
      const hasTierClaims = hasMonthlyTierClaims(progressTask?.tier_claims);
      const taskRewardClaimed =
        progressTask?.reward_claimed === true ||
        progressEntry?.reward_claimed === true;
      return tiers.map((tier, index) => {
        const claimState = progressTask?.tier_claims?.[index] ?? {};
        const reward = tier?.rewards?.[0];
        return {
          threshold: Number(tier?.threshold) || 0,
          claimed: isMonthlyRewardClaimed({
            claimState,
            taskRewardClaimed,
            index,
            reachedCount: currentTier,
            hasTierClaims,
          }),
          rewardLabel: formatTierRewardLabel(reward, {
            primaryCurrencyLabel: rewardCurrencyLabel,
            secondaryCurrencyLabel: rewardSecondaryCurrencyLabel,
          }),
        };
      });
    },
    [
      tiers,
      progressTask,
      progressEntry,
      currentTier,
      rewardCurrencyLabel,
      rewardSecondaryCurrencyLabel,
    ],
  );

  const maxTier = milestones.length;
  const summary = useMemo(
    () => getTierProgressSummary(currentTier, maxTier),
    [currentTier, maxTier],
  );

  const title = isLoginChallenge
    ? AppStrings.GAME_PASS_MONTHLY_DAILY_LOGIN_TITLE
    : progressEntry?.name ||
      task?.challenge ||
      task?.name ||
      progressTask?.name ||
      progressTask?.task_name ||
      AppStrings.MONTHLY_CHALLENGES;
  const subtitle = isLoginChallenge
    ? buildLoginStreakSubtitle(
        getLoginResetTimeLabel(
          streakResetHour,
          task?.reset_time,
          task?.login_reset_time,
          progressTask?.reset_time,
          progressTask?.login_reset_time,
          progressEntry?.reset_time,
        ),
      )
    : progressEntry?.description ||
      task?.description ||
      progressTask?.description ||
      "";
  // FE-2557: mirror the client widget's readout ("5/6 to next reward ·
  // Tier 1 of 3") instead of count over the final threshold ("5/10") — CS
  // agents compare this card against the player's screen. Non-tiered tasks
  // keep the plain count/goal counter.
  const nextReward = getNextRewardSummary(
    count,
    milestones.map((milestone) => milestone.threshold),
    currentTier,
  );
  const counterLabel = nextReward?.counterText ?? `${count}/${goal}`;
  const tierLabel = nextReward?.tierText;
  const completed =
    progressTask?.completed === true ||
    (maxTier > 0 && currentTier >= maxTier);
  // Manual pass can complete a task with a raw count below the last threshold.
  // The header already caps at that threshold; the bar fill must too, otherwise
  // a completed card reads "5/5" while the track stays empty.
  const lastThreshold =
    Number(milestones[milestones.length - 1]?.threshold) || goal;
  const barCount = completed
    ? Math.max(count, lastThreshold)
    : isLoginChallenge
      ? Math.max(count, unlockCount)
      : count;
  let challengeStatus = {
    label: AppStrings.GAME_PASS_MONTHLY_STATUS_NOT_STARTED,
    tone: "not-started",
  };
  if (completed) {
    challengeStatus = { label: AppStrings.COMPLETED, tone: "completed" };
  } else if (currentTier > 0 || count > 0) {
    challengeStatus = { label: AppStrings.IN_PROGRESS, tone: "in-progress" };
  }

  const isPending = incrementMutation.isPending || passMutation.isPending;

  const loginHistoryColumns = useMemo(() => {
    if (!isLoginChallenge) return historyColumns;
    return historyColumns.map((column, index) => {
      const label = typeof column === "string" ? column : column.label;
      return {
        label,
        sortKey:
          (typeof column === "object" && column.sortKey) ||
          LOGIN_STREAK_HISTORY_SORT_KEYS[index],
      };
    });
  }, [historyColumns, isLoginChallenge]);

  const loginHistoryRows = useMemo(() => {
    if (!isLoginChallenge) return historyRows;
    const resolvedRows =
      streakDays.length > 0
        ? buildLoginStreakHistoryRows({
            streakDays,
            progressTask,
            streakResetHour,
            now: viewNow,
          })
        : historyRows;
    return decorateLoginStreakHistoryRows(resolvedRows, {
      milestones,
      onWindowExpired: handleWindowExpired,
    });
  }, [
    handleWindowExpired,
    historyRows,
    isLoginChallenge,
    milestones,
    progressTask,
    streakDays,
    streakResetHour,
    viewNow,
  ]);

  const loginSummary = useMemo(
    () =>
      isLoginChallenge
        ? buildLoginStreakSummary({
            progressTask,
            historyRows,
            streakDays,
            streakResetHour,
            now: viewNow,
          })
        : null,
    [
      isLoginChallenge,
      progressTask,
      historyRows,
      streakDays,
      streakResetHour,
      viewNow,
    ],
  );

  if (readOnly) {
    return (
      <ReadOnlyChallengeCard
        challenge={{
          id: challengeId,
          title,
          subtitle,
          counterLabel,
          tierLabel,
          count: barCount,
          goal,
          milestones: milestones.map((milestone) => {
            const reward = tiers.find(
              (tier) => Number(tier.threshold) === milestone.threshold,
            )?.rewards?.[0];
            return {
              threshold: milestone.threshold,
              rewardAmount: Number(reward?.amount) || 0,
              rewardCurrency: reward?.currency,
              rewardType: reward?.reward_type,
              claimed: milestone.claimed,
            };
          }),
        }}
        status={challengeStatus}
        testId={testId}
      />
    );
  }

  const handleSave = async ({ mode, tier, count: targetCount, reason }) => {
    if (!userId || !taskId) {
      message.error(AppStrings.GAME_PASS_MONTHLY_PROGRESS_SAVE_ERROR);
      return;
    }

    if (mode === EDIT_MODE_STEPS) {
      if (targetCount < count) {
        message.warning(
          AppStrings.GAME_PASS_MONTHLY_PROGRESS_DECREASE_UNSUPPORTED,
        );
        return;
      }
      if (targetCount === count) {
        return;
      }
      try {
        await incrementMutation.mutateAsync({
          userId,
          taskId,
          campaignId,
          reason,
          amount: targetCount - count,
        });
        if (isLoginChallenge) {
          await streakHistoryQuery.refetch();
        }
        message.success(AppStrings.GAME_PASS_MONTHLY_PROGRESS_SAVE_SUCCESS);
      } catch (error) {
        message.error(
          getApiErrorMessage(
            error,
            AppStrings.GAME_PASS_MONTHLY_PROGRESS_SAVE_ERROR,
          ),
        );
      }
      return;
    }

    if (tier < currentTier) {
      message.warning(
        AppStrings.GAME_PASS_MONTHLY_PROGRESS_DECREASE_UNSUPPORTED,
      );
      return;
    }
    if (tier === currentTier) {
      return;
    }

    try {
      if (tier >= maxTier && !completed) {
        await passMutation.mutateAsync({
          userId,
          taskId,
          campaignId,
          challengeId,
          reason,
        });
      } else {
        const targetThreshold = Number(tiers[tier - 1]?.threshold);
        const amount = targetThreshold - count;
        if (!Number.isFinite(targetThreshold) || amount <= 0) {
          message.warning(
            AppStrings.GAME_PASS_MONTHLY_PROGRESS_DECREASE_UNSUPPORTED,
          );
          return;
        }
        await incrementMutation.mutateAsync({
          userId,
          taskId,
          campaignId,
          reason,
          amount,
        });
      }
      if (isLoginChallenge) {
        await streakHistoryQuery.refetch();
      }
      message.success(AppStrings.GAME_PASS_MONTHLY_PROGRESS_SAVE_SUCCESS);
    } catch (error) {
      message.error(
        getApiErrorMessage(
          error,
          AppStrings.GAME_PASS_MONTHLY_PROGRESS_SAVE_ERROR,
        ),
      );
    }
  };

  return (
    <MonthlyChallengeCard
      title={title}
      subtitle={subtitle}
      counterLabel={counterLabel}
      tierLabel={tierLabel}
      status={challengeStatus}
      testId={testId}
    >
      {isLoginChallenge && (
        <LoginStreakSummaryStrip
          summary={loginSummary}
          testId={`${testId}-login-summary`}
        />
      )}
      <MonthlyTierProgressBar
        progressLabel={AppStrings.USER_GAME_PASS_MONTHLY_CURRENT_TIER_PROGRESS}
        challengesText={summary.challengesText}
        percentLabel={summary.percentLabel}
        milestones={milestones}
        reachedCount={currentTier}
        count={barCount}
        goal={goal}
        onEditProgress={isPending ? undefined : () => setIsEditOpen(true)}
        testId={`${testId}-tier`}
      />
      {(historyLabel || historyColumns.length > 0) && (
        <MonthlyHistorySection
          label={historyLabel}
          columns={isLoginChallenge ? loginHistoryColumns : historyColumns}
          rows={isLoginChallenge ? loginHistoryRows : historyRows}
          emptyText={historyEmptyText}
          rowTestIdPrefix={`${testId}-history-row`}
          searchPlaceholder={historySearchPlaceholder}
          searchFilters={historySearchFilters}
          countedFilterLabel={historyCountedFilterLabel}
          sortable={isLoginChallenge}
          legend={isLoginChallenge ? <LoginStreakHistoryLegend /> : undefined}
          footnote={
            isLoginChallenge
              ? AppStrings.GAME_PASS_MONTHLY_LOGIN_HISTORY_FOOTNOTE
              : undefined
          }
          testId={`${testId}-history`}
        />
      )}
      <EditProgressModal
        open={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        currentTier={currentTier}
        maxTier={maxTier}
        currentCount={count}
        goal={goal}
        milestones={milestones}
        onSave={handleSave}
        testId={`${testId}-edit-modal`}
      />
    </MonthlyChallengeCard>
  );
};

export default LiveMonthlyChallengeCard;
